/**
 * Date handling for the app.
 *
 * The single most important rule here: Postgres `date` columns come back as
 * "YYYY-MM-DD" strings, and `new Date("2026-05-25")` parses those as **UTC
 * midnight**. Any timezone west of UTC then renders or compares them as the
 * previous day. That bug has bitten this codebase twice — once turning CI red
 * on export snapshots, once silently flagging on-time materials as overdue.
 *
 * So: never call `new Date()` directly on a date-only column. Use
 * `parseDateOnly()`. Reserve `new Date()` for `timestamptz` columns
 * (created_at, sent_at, scheduled_for, …) where localizing is correct.
 */

/** Matches a bare "YYYY-MM-DD", optionally with a time component after it. */
const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})/;

/** True when the string is exactly a date, with no time component. */
export function isDateOnly(iso: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(iso);
}

/**
 * Parse a date-only string into a Date at **local** midnight, so comparisons
 * against `new Date()` behave the way a human means them.
 * Returns an invalid Date for unparseable input (check with Number.isNaN).
 */
export function parseDateOnly(iso: string): Date {
  const m = DATE_ONLY_RE.exec(iso);
  if (!m) return new Date(NaN);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return new Date(NaN);
  return new Date(Number(m[1]), month - 1, day);
}

/** Midnight of the given date, in local time. Defaults to today. */
export function startOfDay(d: Date = new Date()): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * Is a date-only deadline in the past relative to `today`?
 *
 * "Due today" is NOT overdue — that's the distinction the old
 * `new Date(iso) < today` comparison got wrong in western timezones.
 */
export function isPastDue(iso: string | null | undefined, today: Date = new Date()): boolean {
  if (!iso) return false;
  const due = parseDateOnly(iso);
  if (Number.isNaN(due.getTime())) return false;
  return due < startOfDay(today);
}
